import React from 'react';
import { ConcernCard as ConcernCardType } from '@/types/components/whatIsYourConcern';
import RichText from '@/components/ui/RichText/RichText';
import R2Image from '@/components/ui/R2Image';

type Props = {
  item: ConcernCardType;
  isActive: boolean;
  isHovered: boolean;
  shouldScaleDownActive: boolean;
  disableInteraction: boolean;
  onSelect?: () => void;
  onHoverChange?: (value: boolean) => void;
  itemRef?: (element: HTMLDivElement | null) => void;
  overlayClassName?: string;
};

type LegacyImageCard = {
  imageUrl?: string;
};

type NewImageCard = {
  image?: {
    urlDesktop?: string;
    urlMobile?: string;
    alt?: string;
  };
};

const getCardImage = (item: ConcernCardType) => {
  const card = item as ConcernCardType & LegacyImageCard & NewImageCard;

  const imagePath =
    card.image?.urlDesktop || card.image?.urlMobile || card.imageUrl || '';

  const imageAlt = card.image?.alt || item?.heading?.content || 'Concern';

  return {
    imagePath,
    imageAlt,
  };
};

const ConcernCard = ({
  item,
  isActive,
  isHovered,
  shouldScaleDownActive,
  disableInteraction,
  onSelect,
  onHoverChange,
  itemRef,
  overlayClassName,
}: Props) => {
  const title = item?.heading?.content || 'Concern';
  const { imagePath, imageAlt } = getCardImage(item);

  const scaleClass = isActive
    ? shouldScaleDownActive
      ? 'scale-[0.95] z-10'
      : 'scale-[1.05] z-10'
    : isHovered
      ? 'scale-[1.05] z-10'
      : 'scale-[0.95]';

  return (
    <div
      ref={itemRef}
      onClick={disableInteraction ? undefined : onSelect}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      tabIndex={disableInteraction ? -1 : 0}
      className={[
        'relative h-90 shrink-0 overflow-hidden rounded-[10px] focus:outline-none',
        'w-60 md:w-75',
        'transition-transform duration-300 ease-out',
        disableInteraction ? 'pointer-events-none' : 'cursor-pointer',
        scaleClass,
      ].join(' ')}
    >
      {imagePath ? (
        <R2Image
          path={imagePath}
          alt={imageAlt || title}
          width={350}
          height={492}
          className="h-full w-full rounded-lg object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center rounded-lg bg-gray-200">
          <span className="text-sm text-gray-500">No image available</span>
        </div>
      )}

      <div
        className={
          overlayClassName ||
          'bg-surface-overlay absolute right-3 bottom-3 left-3 rounded-lg p-4 text-left font-normal text-white'
        }
      >
        <RichText
          styles={{
            p: 'text-[18px] font-bold text-white',
          }}
        >
          {item.heading.content}
        </RichText>

        {item.button?.content && (
          <RichText
            styles={{
              p: 'inline-flex cursor-pointer text-xs bg-theme-primary rounded',
              className:
                'mt-2 inline-flex rounded bg-theme-primary px-4 py-2 text-xs',
            }}
          >
            {item.button.content}
          </RichText>
        )}
      </div>
    </div>
  );
};

export default ConcernCard;
